import { useMemo, useState, type ReactNode } from "react";

import { useMediaObjectUrl } from "~/hooks/useMediaObjectUrl";
import type { Client, MatrixMediaSource } from "~/lib/matrix";

const POSTER_THUMBNAIL_OPTS = { thumbnail: { w: 800, h: 800, method: "scale" as const } };

export function VideoMedia({
  client,
  source,
  mimetype,
  thumbnailSource,
  thumbnailMimetype,
  width,
  height,
  fallback,
}: {
  client: Client;
  source: MatrixMediaSource;
  mimetype?: string;
  thumbnailSource?: MatrixMediaSource | null;
  thumbnailMimetype?: string | null;
  width?: number;
  height?: number;
  fallback: ReactNode;
}) {
  const [requested, setRequested] = useState(false);
  const [decodeFailed, setDecodeFailed] = useState(false);
  const media = useMediaObjectUrl(client, source, mimetype, { enabled: requested });
  const posterOpts = useMemo(
    () => ({ ...POSTER_THUMBNAIL_OPTS, enabled: !!thumbnailSource }),
    [thumbnailSource],
  );
  const poster = useMediaObjectUrl(
    client,
    thumbnailSource ?? source,
    thumbnailSource ? (thumbnailMimetype ?? "image/jpeg") : mimetype,
    posterOpts,
  );
  if (decodeFailed) return <>{fallback}</>;
  if (!requested) {
    if (thumbnailSource && poster.status === "ready") {
      return (
        <VideoPoster
          posterSrc={poster.src}
          width={width}
          height={height}
          onClick={() => setRequested(true)}
        />
      );
    }
    return <MediaLoadButton label="Load video" onClick={() => setRequested(true)} />;
  }
  if (media.status === "failed") return <>{fallback}</>;
  if (media.status === "loading") return <MediaLoading label="Loading video" />;
  return (
    <video
      controls
      muted
      playsInline
      preload="metadata"
      src={media.src}
      onError={() => setDecodeFailed(true)}
      style={{
        display: "block",
        maxWidth: "var(--chat-file-attachment-max-width)",
        width: "100%",
        borderRadius: "var(--chat-radius-md)",
        background: "var(--chat-surface-raised)",
      }}
    />
  );
}

export function AudioMedia({
  client,
  source,
  mimetype,
  fallback,
}: {
  client: Client;
  source: MatrixMediaSource;
  mimetype?: string;
  fallback: ReactNode;
}) {
  const [requested, setRequested] = useState(false);
  const [decodeFailed, setDecodeFailed] = useState(false);
  const media = useMediaObjectUrl(client, source, mimetype, { enabled: requested });
  if (decodeFailed) return <>{fallback}</>;
  if (!requested) return <MediaLoadButton label="Load audio" onClick={() => setRequested(true)} />;
  if (media.status === "failed") return <>{fallback}</>;
  if (media.status === "loading") return <MediaLoading label="Loading audio" />;
  return (
    <audio
      controls
      preload="metadata"
      src={media.src}
      onError={() => setDecodeFailed(true)}
      style={{
        display: "block",
        width: "100%",
        maxWidth: "var(--chat-file-attachment-max-width)",
      }}
    />
  );
}

function VideoPoster({
  posterSrc,
  width,
  height,
  onClick,
}: {
  posterSrc: string;
  width?: number;
  height?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="chat-button"
      aria-label="Play video"
      style={{
        position: "relative",
        display: "block",
        maxWidth: "var(--chat-file-attachment-max-width)",
        width: "100%",
        aspectRatio: width && height ? `${width} / ${height}` : undefined,
        padding: 0,
        border: "none",
        borderRadius: "var(--chat-radius-md)",
        overflow: "hidden",
        background: "var(--chat-surface-raised)",
        cursor: "pointer",
      }}
    >
      <img
        src={posterSrc}
        alt=""
        style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
      />
      <span
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "rgba(0, 0, 0, 0.55)",
          }}
        >
          <svg width={20} height={20} viewBox="0 0 24 24" fill="white" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      </span>
    </button>
  );
}

function MediaLoadButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="chat-button"
      style={{
        display: "inline-flex",
        maxWidth: "var(--chat-file-attachment-max-width)",
        padding: "var(--chat-space-2) var(--chat-space-3)",
        border: "none",
        borderRadius: "var(--chat-radius-sm)",
        background: "var(--chat-accent-muted)",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function MediaLoading({ label }: { label: string }) {
  return (
    <span
      role="status"
      style={{
        display: "inline-flex",
        maxWidth: "var(--chat-file-attachment-max-width)",
        padding: "var(--chat-space-2) var(--chat-space-3)",
        borderRadius: "var(--chat-radius-md)",
        background: "var(--chat-surface-raised)",
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
      }}
    >
      {label}
    </span>
  );
}
